import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { compose } from "redux";

import { actions as topikActions } from "../../../../modules/topik";
import { initialAssistStepState } from "../../../../modules/topik/reducers";

const PENDING_STATUSES = ["queued", "inferring"];
// E9: an honest wait has a bound - after this long the panel admits the model may not answer
// and releases the player back to their own thinking.
const SLOW_MS = 20000;

// The graduated AI step companion (§6.5), fully inline - no modal. One quiet button climbs
// the step's ladder (vague nudge -> stronger hint -> read-only idea candidates); the server
// derives the rung from the press count, the client only labels the button. Results land next
// to the input as read-only coaching: the companion never writes into the player's input.
const TopikAssistPanel = ({ t, step, draft, session, aiAssist, dispatch }) => {
  const payload = (session && session.aiAssist) || {};
  const meta = (payload.steps && payload.steps[step]) || null;
  const available = Boolean(payload.available && meta);

  const state = (aiAssist && aiAssist.byStep[step]) || initialAssistStepState;
  const pending = PENDING_STATUSES.includes(state.status);
  // The freshest press count wins: the request/poll result carries used/cap ahead of the
  // (stale-until-next-mutation) session payload; a reopened session falls back to the payload.
  const used = state.used != null ? state.used : (meta && meta.used) || 0;
  const cap = (meta && meta.cap) || 0;
  const exhausted = used >= cap;

  // Restore the latest companion note after a reload/rejoin - the server keeps the chat.
  // Mount-only on purpose: mid-session updates flow through the request/poll actions.
  useEffect(() => {
    if (available && used > 0 && state.status === "idle") {
      dispatch(topikActions.aiAssist.restore({ step }));
    }
  }, []);

  // Track a "this is taking a while" state so a stuck job releases the player gracefully.
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!pending) {
      setSlow(false);
      return undefined;
    }
    const id = setTimeout(() => setSlow(true), SLOW_MS);
    return () => clearTimeout(id);
  }, [pending]);

  if (!available) return null;

  const request = () => dispatch(topikActions.aiAssist.request.request({ step, draft }));

  // The button label telegraphs the climb: on the 3-rung observation ladder the last press is
  // named honestly ("Suggest ideas"); the 2-rung one-liners keep their rungs behind one
  // escalating "Help me (more)" button.
  const triggerLabel = () => {
    if (used === 0) return t("topik-assist.help");
    if (cap > 2 && used === cap - 1) return t("topik-assist.suggest-ideas");
    return t("topik-assist.help-more");
  };

  const hasResult = !pending && Boolean(state.text || state.suggestions.length > 0);

  return (
    <div className="topik-assist">
      {hasResult && (
        <div className="topik-assist__note" role="status">
          <span className="topik-assist__disclosure">{t("topik-assist.disclosure")}</span>
          {state.text && <p className="topik-assist__text">{state.text}</p>}
          {state.suggestions.length > 0 && (
            <ul className="topik-assist__ideas">
              {state.suggestions.map((suggestion) => (
                <li key={suggestion} className="topik-assist__idea">{suggestion}</li>
              ))}
            </ul>
          )}
          {state.question && <p className="topik-assist__question">{state.question}</p>}
        </div>
      )}

      {pending && (
        <p className="topik-assist__pending" aria-live="polite">
          {t("topik-assist.thinking")}
          {slow && <span className="topik-assist__bailout"> {t("topik-assist.bailout")}</span>}
        </p>
      )}

      {state.status === "error" && (
        <p className="topik-assist__error" role="alert">{t("topik-assist.error")}</p>
      )}

      {/* An errored rung is retryable even at the cap - the server re-runs it for free. */}
      {!pending && (state.status === "error" || !exhausted) && (
        <button
          type="button"
          className="topik-analysis__assist-trigger topik-assist__trigger"
          onClick={request}
        >
          {state.status === "error" ? t("topik-assist.retry") : triggerLabel()}
        </button>
      )}

      {!pending && exhausted && state.status !== "error" && hasResult && (
        <p className="topik-assist__cap">{t("topik-assist.cap-reached")}</p>
      )}
    </div>
  );
};

TopikAssistPanel.propTypes = {
  t: PropTypes.func,
  step: PropTypes.string.isRequired,
  draft: PropTypes.string,
  session: PropTypes.object,
  aiAssist: PropTypes.object,
  dispatch: PropTypes.func,
};

// The HOCs inject t/session/aiAssist/dispatch at runtime; the defaults keep the panel inert
// (and warning-free) when a parent is rendered with those layers mocked away in tests.
TopikAssistPanel.defaultProps = {
  t: (k) => k,
  draft: "",
  session: null,
  aiAssist: null,
  dispatch: () => {},
};

const mapStateToProps = (state) => ({
  session: state.topik.session,
  aiAssist: state.topik.aiAssist,
});

export default compose(withTranslation("views"), connect(mapStateToProps))(TopikAssistPanel);
